/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Vang Pham (ptvang@rsupport.com)
 *
 * @description
 *
 * @created at Fri Oct 11 2024
 **/

import { useMemo, useRef, useState } from "react";
import { useTheme } from "@emotion/react";
import { useQuery } from "@tanstack/react-query";
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";

import { _T } from "@rfice/language";

import { useProperties } from "@/frameworks/libraries/properties";
import { useFileManager } from "@/frameworks/core/hooks";
import { Column, Image, Button } from "@/frameworks/designStuff/components";
import { wrapThumbnailMobile, FileViewerMobileThumbnailWrap, ThumbnaiButtonMobile, styleImageViewer } from "./emotion";
import { FileViewerMobileWrap, LoadingFile } from "../../emotion";
import { MIN_SCALE_MOBILE, MAX_SCALE_MOBILE } from "../constant";

import image172x102 from "@/assets/images/loadingFailed/thumbnail.png";
import image1280x720 from "@/assets/images/loadingFailed/content.png";

export default function FileViewerMobile({ fileID }: { fileID: string }) {
	const [currentPage, setCurrentPage] = useState(0);
	const [isActiveThumbnail, setIsActiveThumbnail] = useState(true);

	const transformWrapperRef = useRef<ReactZoomPanPinchRef>(null);

	const properties = useProperties();
	const { convertDocToImage, progressDocImage } = useFileManager();
	const theme = useTheme();

	/* Load File */
	const { data: convertResult } = useQuery({
		queryKey: ["convertDocToImage", fileID],
		queryFn: () => convertDocToImage({ fileId: fileID }),
		refetchOnWindowFocus: false
	});

	const { data: progress } = useQuery({
		queryKey: ["progressDocImage", convertResult?.docImageId],
		queryFn: () => progressDocImage({ id: convertResult!.docImageId }),
		enabled: Boolean(convertResult),
		refetchInterval: (data) => (!data || data.complete !== data.total ? 1000 : false),
		refetchOnWindowFocus: false
	});

	const thumbnailList = useMemo(() => {
		if (!convertResult || !progress) return [];
		return Array.from({ length: progress.total }, (_, index) =>
			index < progress.complete ? `/filebox/doc_image/${convertResult.docImageId}/thumbnail?no=${index + 1}` : ""
		);
	}, [progress, convertResult]);

	const imageSrcList = useMemo(() => {
		if (!convertResult || !progress) return [];
		return Array.from({ length: progress.total }, (_, index) =>
			index < progress.complete ? `/filebox/doc_image/${convertResult.docImageId}?no=${index + 1}` : ""
		);
	}, [progress, convertResult]);

	if (!progress || progress.complete === 0)
		return (
			<LoadingFile typoSize="Title3_B" typoColor="neutral_00_100">
				{_T("로딩중")}
			</LoadingFile>
		);

	return (
		<>
			<ThumbnaiButtonMobile>
				<Button.Icon
					name="LeftPanel24"
					filled="primary"
					size="Xlarge"
					css={{
						background: "none",
						padding: "9px 8px",
						backgroundColor: isActiveThumbnail ? "#ffffff29" : "none"
					}}
					onClick={() => setIsActiveThumbnail((prev) => !prev)}
				/>
			</ThumbnaiButtonMobile>
			<FileViewerMobileThumbnailWrap css={{ visibility: isActiveThumbnail ? "visible" : "hidden" }}>
				<Column gap={10} css={{ width: "100%" }}>
					{thumbnailList?.map((image, index) => {
						return (
							<Column
								key={index}
								css={wrapThumbnailMobile(currentPage, index, theme.colors.accent_50_100)}
								onClick={() => setCurrentPage(index)}
							>
								<Image
									src={`${properties.default.webDomain}${image}`}
									alt={image}
									errorSrc={image172x102}
								/>
							</Column>
						);
					})}
				</Column>
			</FileViewerMobileThumbnailWrap>
			<FileViewerMobileWrap>
				<TransformWrapper
					ref={transformWrapperRef}
					minScale={MIN_SCALE_MOBILE}
					maxScale={MAX_SCALE_MOBILE}
					doubleClick={{ mode: "zoomIn", step: 1 }}
					limitToBounds={true}
					centerOnInit={true}
					pinch={{ disabled: false, step: 0.05 }}
				>
					<TransformComponent>
						<Image
							draggable={false}
							src={
								imageSrcList && imageSrcList[currentPage]
									? `${properties.default.webDomain}${imageSrcList[currentPage]}`
									: image1280x720
							}
							css={styleImageViewer}
							errorSrc={image1280x720}
							onError={(e) => console.log("#FileViewer#", "Failed to load image", e)}
							onLoad={() => transformWrapperRef.current?.centerView(1, 0)}
						/>
					</TransformComponent>
				</TransformWrapper>
			</FileViewerMobileWrap>
		</>
	);
}
